/**
 * A subtle WebAudio chime for new alerts (off by default). Two soft sine notes; priority alerts
 * repeat once. No audio files, no network.
 */
import type { AlertSeverity } from '@apos/shared';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx ??= new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function note(a: AudioContext, freq: number, start: number, dur: number, peak: number): void {
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(a.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

export function playChime(severity: AlertSeverity): void {
  const a = audio();
  if (!a) return;
  const t0 = a.currentTime + 0.01;
  const peak = severity === 'warning' ? 0.09 : 0.06;
  note(a, 1046.5, t0, 0.16, peak);
  note(a, 784, t0 + 0.14, 0.24, peak);
  if (severity === 'warning') {
    note(a, 1046.5, t0 + 0.5, 0.16, peak);
    note(a, 784, t0 + 0.64, 0.24, peak);
  }
}

/** Call from a user gesture (e.g. enabling the chime) so browsers allow later playback. */
export function unlockAudio(): void {
  audio();
}
